import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import { formsApi, formatApiError } from "@/lib/api";
import { ArrowRight, Check, Users, FileText, Network, Mail } from "lucide-react";

const TABS = [
  { id: "join", label: "Join the Alliance", icon: Users, hash: "#join" },
  { id: "contribution", label: "Submit Contribution", icon: FileText, hash: "#contribution" },
  { id: "collaboration", label: "Propose Collaboration", icon: Network, hash: "#collaboration" },
  { id: "contact", label: "Contact BSCA", icon: Mail, hash: "#contact" },
];

const TAB_INTRO = {
  join: { eyebrow: "Membership", title: "Step into the alliance.", sub: "Membership is free and open globally. Choose the role that fits — and tell us about your inquiry." },
  contribution: { eyebrow: "Contribute", title: "Share your work with BSCA.", sub: "Forewords, endorsements, research, reflections, talks, partnerships — we welcome serious contributions from researchers, technologists, authors, and readers." },
  collaboration: { eyebrow: "Collaborate", title: "Propose research or partnership.", sub: "Bring your inquiry to the alliance — for joint research, labs, talks, summits, whitepapers, or institutional partnership." },
  contact: { eyebrow: "Contact", title: "Begin a conversation.", sub: "For collaboration, media, partnership, or general inquiry. We read every message. You can also email us directly at info@bscalliance.org." },
};

const AREAS = [
  "Consciousness Research", "Neuroscience", "Quantum Physics", "Cosmology", "Artificial Intelligence",
  "Ethics & Future Technology", "Philosophy of Science", "Spirituality & Wisdom Traditions",
  "Beyond Science Book Series", "Research Collaboration", "Student Circle", "Media / Talks / Events", "Other",
];
const MEMBER_TYPES = [
  { v: "Research Fellow", desc: "Working researchers, post-docs, and faculty contributing to BSCA's open inquiry." },
  { v: "Ambassador", desc: "Regional representatives carrying BSCA's mission into local communities." },
  { v: "Institutional Partner", desc: "Universities, research institutes, and aligned organizations." },
  { v: "Student Circle", desc: "Undergraduates and graduate students leading campus reading and inquiry groups." },
  { v: "Reader / Enthusiast", desc: "Serious general readers, authors, and the intellectually curious public." },
];

const CONTRIBUTION_TYPES = [
  "Foreword / Endorsement", "Research Contribution", "Book Reflection", "Article / Essay",
  "Media / Talk Proposal", "Collaboration Proposal", "Institutional Partnership", "Student Contribution", "Other",
];
const RELATED_BOOK = [
  "Beyond Science: The Edge of the Universe",
  "Beyond Science: The Mystery of Consciousness",
  "Beyond Science: The Hidden Dimensions of Reality",
  "Beyond Science: The Future of Life and Intelligence",
  "Beyond Science: When Science Meets Spirit",
  "Science + Consciousness Alliance Manifesto",
  "Research Compendium",
  "General BSCA Topic",
  "Other",
];

const COLLAB_TYPES = [
  "Research Collaboration", "Institutional Partnership", "Beyond Labs", "Beyond Talks",
  "Whitepaper Collaboration", "Student Circle", "Event / Summit Partnership",
  "Media / Interview Collaboration", "Publishing / Book Collaboration", "Other",
];
const RESEARCH_DOMAINS_OPTS = [
  "Cosmology & Fundamental Physics",
  "Consciousness & Neuroscience",
  "Quantum Reality & Hidden Dimensions",
  "AI, Life & Intelligence",
  "Science-Spirit Integration",
  "Ethics & Future Technology",
  "Education & Public Dialogue",
  "Other",
];

const INQUIRY_TYPES = [
  "General Inquiry", "Media Inquiry", "Speaking Invitation", "Book / Publication Inquiry",
  "Membership Support", "Partnership Inquiry", "Research Inquiry", "Technical / Website Issue", "Other",
];

export default function Engage() {
  const location = useLocation();
  const navigate = useNavigate();

  const activeId = useMemo(() => {
    const h = (location.hash || "").replace("#", "");
    return TABS.find((t) => t.id === h)?.id || "join";
  }, [location.hash]);

  useEffect(() => {
    if (!location.hash) navigate("/engage#join", { replace: true });
  }, [location.hash, navigate]);

  const switchTab = (id) => {
    navigate(`/engage#${id}`);
    // smoothly scroll to forms section
    requestAnimationFrame(() => {
      const el = document.getElementById("engage-forms");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const intro = TAB_INTRO[activeId];

  return (
    <PublicLayout title="Engage with BSCA">
      {/* Dark cosmic hero */}
      <section className="relative bsca-dark bsca-cosmic bsca-grain -mt-16 md:-mt-20 lg:-mt-[88px] pt-32 md:pt-40 pb-20 md:pb-24 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 text-center bsca-fade-up">
          <SectionEyebrow dark>Engage with BSCA</SectionEyebrow>
          <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-[5.5rem] tracking-tight leading-[1.02]">
            One alliance. <span className="text-[#D4AF37]">Four ways in.</span>
          </h1>
          <p className="mt-6 text-white/75 text-lg max-w-2xl mx-auto">
            Join the alliance, submit a contribution, propose a collaboration, or reach the BSCA team. Membership is free and global.
          </p>
          {/* Tab bar in hero — 2x2 grid on mobile, inline on md+ */}
          <div className="mt-10 md:mt-12" data-testid="engage-tabs">
            <div className="grid grid-cols-2 md:flex md:flex-wrap md:justify-center gap-2 md:gap-3 max-w-md md:max-w-none mx-auto">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = activeId === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`engage-tab-${t.id}`}
                    onClick={() => switchTab(t.id)}
                    className={`bsca-lift inline-flex items-center justify-center md:justify-start gap-2 px-4 md:px-5 py-3 rounded-sm text-[13px] md:text-sm font-medium border transition-all whitespace-nowrap ${
                      active
                        ? "bg-[#D4AF37] text-[#050B14] border-[#D4AF37]"
                        : "bg-white/5 text-white/85 border-white/15 hover:border-[#D4AF37]/60 hover:text-white"
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.6} />
                    <span className="truncate">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Form area — light */}
      <section id="engage-forms" className="bg-[#F8F6F1] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 md:mb-14">
            <SectionEyebrow>{intro.eyebrow}</SectionEyebrow>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight">{intro.title}</h2>
            <p className="mt-4 text-[#4B5563] max-w-2xl mx-auto">{intro.sub}</p>
          </div>

          {activeId === "join" && <MembershipForm />}
          {activeId === "contribution" && <ContributionForm />}
          {activeId === "collaboration" && <CollaborationForm />}
          {activeId === "contact" && <ContactForm />}
        </div>
      </section>

      <section className="bg-white py-14 border-t border-black/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-6 text-sm">
          <Link to="/manifesto" className="group flex items-center gap-3 text-[#1f2937]">
            <span className="bsca-eyebrow text-[#7A5A06]">Read</span>
            <span className="bsca-link group-hover:text-[#7A5A06]">The BSCA Manifesto <ArrowRight className="inline h-3 w-3 ml-1" /></span>
          </Link>
          <Link to="/research-compendium" className="group flex items-center gap-3 text-[#1f2937]">
            <span className="bsca-eyebrow text-[#7A5A06]">Explore</span>
            <span className="bsca-link group-hover:text-[#7A5A06]">Research Compendium <ArrowRight className="inline h-3 w-3 ml-1" /></span>
          </Link>
          <Link to="/beyond-science" className="group flex items-center gap-3 text-[#1f2937]">
            <span className="bsca-eyebrow text-[#7A5A06]">Read</span>
            <span className="bsca-link group-hover:text-[#7A5A06]">Beyond Science Series <ArrowRight className="inline h-3 w-3 ml-1" /></span>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}

/* ---------------- Reusable field components ---------------- */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function FieldErr({ message, testid }) {
  if (!message) return null;
  return <div data-testid={testid} className="mt-1 text-[12px] text-red-700">{message}</div>;
}
function FieldShell({ children }) {
  return <label className="block">{children}</label>;
}
function Label({ children, required }) {
  return <span className="bsca-eyebrow text-[#7A5A06] text-[10px]">{children}{required && " *"}</span>;
}
function Field({ label, required, value, onChange, type = "text", testid, placeholder, autoComplete, error }) {
  return (
    <FieldShell>
      <Label required={required}>{label}</Label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        aria-invalid={error ? "true" : "false"}
        className={`mt-1.5 w-full bg-white border px-4 py-3 rounded-sm focus:outline-none focus:ring-1 text-[#050B14] placeholder-black/35 ${
          error ? "border-red-500 focus:border-red-600 focus:ring-red-500" : "border-black/15 focus:border-[#050B14] focus:ring-[#050B14]"
        }`}
      />
      <FieldErr message={error} testid={testid ? `${testid}-error` : undefined} />
    </FieldShell>
  );
}
function Select({ label, required, value, onChange, options, testid, error }) {
  return (
    <FieldShell>
      <Label required={required}>{label}</Label>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        aria-invalid={error ? "true" : "false"}
        className={`mt-1.5 w-full bg-white border px-4 py-3 rounded-sm focus:outline-none focus:ring-1 text-[#050B14] appearance-none ${
          error ? "border-red-500 focus:border-red-600 focus:ring-red-500" : "border-black/15 focus:border-[#050B14] focus:ring-[#050B14]"
        }`}
      >
        <option value="">Select…</option>
        {options.map((o) => (typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.v} value={o.v}>{o.v}</option>))}
      </select>
      <FieldErr message={error} testid={testid ? `${testid}-error` : undefined} />
    </FieldShell>
  );
}
function TextArea({ label, required, value, onChange, testid, placeholder, rows = 5, error }) {
  return (
    <FieldShell>
      <Label required={required}>{label}</Label>
      <textarea
        data-testid={testid}
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-invalid={error ? "true" : "false"}
        className={`mt-1.5 w-full bg-white border px-4 py-3 rounded-sm focus:outline-none focus:ring-1 text-[#050B14] placeholder-black/35 resize-none ${
          error ? "border-red-500 focus:border-red-600 focus:ring-red-500" : "border-black/15 focus:border-[#050B14] focus:ring-[#050B14]"
        }`}
      />
      <FieldErr message={error} testid={testid ? `${testid}-error` : undefined} />
    </FieldShell>
  );
}
function Consent({ checked, onChange, label, testid, error }) {
  return (
    <div>
      <label className="flex items-start gap-3 text-sm text-[#1f2937] cursor-pointer">
        <input data-testid={testid} type="checkbox" checked={checked} onChange={onChange} className="mt-1 h-4 w-4 accent-[#D4AF37]" />
        <span>{label}</span>
      </label>
      <FieldErr message={error} testid={testid ? `${testid}-error` : undefined} />
    </div>
  );
}

// Tiny helper used per form. Pass an object whose values are an array
// of [predicate, "message"] pairs; returns either {} on success or
// { field: "message" } map.
function runFieldValidation(rules) {
  const errs = {};
  for (const [key, checks] of Object.entries(rules)) {
    for (const [predOk, msg] of checks) {
      if (!predOk) { errs[key] = msg; break; }
    }
  }
  return errs;
}
function FormError({ message, testid }) {
  if (!message) return null;
  return (
    <div data-testid={testid} role="alert" className="error text-sm rounded-sm border border-red-500/30 bg-red-50 text-red-700 px-4 py-3">
      {message}
    </div>
  );
}
function SuccessCard({ title, body, onAnother, testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-black/10 max-w-2xl mx-auto p-10 text-center">
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10">
        <Check className="h-6 w-6 text-[#D4AF37]" />
      </div>
      <h3 className="mt-6 font-serif text-3xl tracking-tight">{title}</h3>
      <p className="mt-3 text-[#4B5563] leading-relaxed">{body}</p>
      <button onClick={onAnother} className="mt-8 bsca-link text-sm">Submit another</button>
    </div>
  );
}

/* ---------------- FORM 1 — Membership ---------------- */
function MembershipForm() {
  const initial = {
    full_name: "", email: "", country: "", city: "",
    organization: "", role: "", area_of_interest: "",
    membership_type: "Reader / Enthusiast", motivation: "",
    charter_consent: false, communication_consent: false,
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fe, setFe] = useState({});
  const [done, setDone] = useState(false);
  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
    if (fe[k]) setFe({ ...fe, [k]: undefined });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const errs = runFieldValidation({
      full_name: [[form.full_name.trim().length > 1, "Please enter your full name."]],
      email: [
        [!!form.email.trim(), "Email is required."],
        [EMAIL_RE.test(form.email.trim()), "Enter a valid email address."],
      ],
      country: [[!!form.country.trim(), "Please enter your country."]],
      area_of_interest: [[!!form.area_of_interest, "Please select an area of interest."]],
      membership_type: [[!!form.membership_type, "Please select a membership type."]],
      motivation: [[form.motivation.trim().length >= 10, "Please share a short motivation (at least 10 characters)."]],
      charter_consent: [[form.charter_consent, "Please agree to the BSCA Charter of Inquiry."]],
      communication_consent: [[form.communication_consent, "Please consent to BSCA communications."]],
    });
    setFe(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await formsApi.submit("membership", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "We couldn't submit right now. Please try again in a moment.");
    } finally { setBusy(false); }
  };

  if (done) return <SuccessCard testid="membership-success" title="Welcome to BSCA." body="Your membership request has been received. Our team will be in touch with next steps as the alliance opens its cohorts." onAnother={() => { setForm(initial); setDone(false); setFe({}); }} />;

  return (
    <div className="grid md:grid-cols-12 gap-8 md:gap-10">
      {/* Sidebar: membership types */}
      <aside className="md:col-span-5">
        <SectionEyebrow>Membership Categories</SectionEyebrow>
        <div className="space-y-3" data-testid="membership-categories">
          {MEMBER_TYPES.map((t) => {
            const active = form.membership_type === t.v;
            return (
              <button
                type="button"
                key={t.v}
                data-testid={`membership-type-${t.v.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setForm({ ...form, membership_type: t.v })}
                className={`w-full text-left p-5 border transition-all bsca-lift ${
                  active ? "bg-white border-[#D4AF37] shadow-md" : "bg-white/60 border-black/10 hover:border-black/20"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-lg">{t.v}</div>
                    <div className="text-sm text-[#4B5563] mt-1">{t.desc}</div>
                  </div>
                  {active && <Check className="h-4 w-4 mt-1 text-[#D4AF37]" />}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      <form onSubmit={submit} data-testid="membership-form" className="md:col-span-7 bg-white border border-black/10 p-7 md:p-10 space-y-6 rounded-sm">
        <SectionEyebrow>Your details</SectionEyebrow>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Full Name" required value={form.full_name} onChange={update("full_name")} testid="form-full-name" autoComplete="name" error={fe.full_name} />
          <Field label="Email Address" type="email" required value={form.email} onChange={update("email")} testid="form-email" autoComplete="email" error={fe.email} />
          <Field label="Country / Region" required value={form.country} onChange={update("country")} testid="form-country" autoComplete="country-name" error={fe.country} />
          <Field label="City" value={form.city} onChange={update("city")} testid="form-city" />
          <Field label="Organization / Institution" value={form.organization} onChange={update("organization")} testid="form-organization" />
          <Field label="Role / Designation" value={form.role} onChange={update("role")} testid="form-role" />
          <Select label="Primary Area of Interest" required value={form.area_of_interest} onChange={update("area_of_interest")} options={AREAS} testid="form-area" error={fe.area_of_interest} />
          <Select label="Membership Type" required value={form.membership_type} onChange={update("membership_type")} options={MEMBER_TYPES.map((m) => m.v)} testid="form-membership-type" error={fe.membership_type} />
        </div>
        <TextArea label="Motivation for Joining BSCA" required value={form.motivation} onChange={update("motivation")} testid="form-motivation" placeholder="What draws you to the alliance?" error={fe.motivation} />
        <div className="space-y-3 pt-2">
          <Consent
            testid="form-charter-consent"
            checked={form.charter_consent}
            onChange={update("charter_consent")}
            label="I agree to uphold the BSCA Charter of Inquiry — rigorous, non-dogmatic, ethical, and respectful of both empirical evidence and experiential wisdom."
            error={fe.charter_consent}
          />
          <Consent
            testid="form-consent"
            checked={form.communication_consent}
            onChange={update("communication_consent")}
            label="I consent to receive occasional BSCA communications and to my information being stored per the Privacy Policy."
            error={fe.communication_consent}
          />
        </div>
        <FormError testid="form-error" message={err} />
        <button type="submit" disabled={busy} data-testid="btn-submit-membership" className="bsca-lift inline-flex items-center bg-[#050B14] text-white font-medium px-8 py-4 rounded-sm hover:bg-[#0A1120] disabled:opacity-60">
          {busy ? "Submitting…" : "Join the Alliance"} <ArrowRight className="h-4 w-4 ml-2" />
        </button>
      </form>
    </div>
  );
}

/* ---------------- FORM 2 — Contribution ---------------- */
function ContributionForm() {
  const initial = {
    full_name: "", email: "", country: "", organization: "", role: "",
    contribution_type: "", related_topic: "", title: "", message: "",
    consent: false,
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fe, setFe] = useState({});
  const [done, setDone] = useState(false);
  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
    if (fe[k]) setFe({ ...fe, [k]: undefined });
  };
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const errs = runFieldValidation({
      full_name: [[form.full_name.trim().length > 1, "Please enter your full name."]],
      email: [
        [!!form.email.trim(), "Email is required."],
        [EMAIL_RE.test(form.email.trim()), "Enter a valid email address."],
      ],
      country: [[!!form.country.trim(), "Please enter your country."]],
      contribution_type: [[!!form.contribution_type, "Please select a contribution type."]],
      title: [[form.title.trim().length > 1, "Please enter a title for your submission."]],
      message: [[form.message.trim().length >= 20, "Please share a longer message (at least 20 characters)."]],
      consent: [[form.consent, "Please grant BSCA permission to review your contribution."]],
    });
    setFe(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await formsApi.submit("contribution", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "We couldn't submit right now. Please try again in a moment.");
    } finally { setBusy(false); }
  };
  if (done) return <SuccessCard testid="contribution-success" title="Thank you." body="Your contribution has been received. The BSCA editorial team reviews submissions in cycles and will respond as appropriate." onAnother={() => { setForm(initial); setDone(false); setFe({}); }} />;
  return (
    <form onSubmit={submit} data-testid="contribution-form" className="max-w-4xl mx-auto bg-white border border-black/10 p-7 md:p-10 space-y-6 rounded-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full Name" required value={form.full_name} onChange={update("full_name")} testid="form-c-name" autoComplete="name" error={fe.full_name} />
        <Field label="Email Address" type="email" required value={form.email} onChange={update("email")} testid="form-c-email" autoComplete="email" error={fe.email} />
        <Field label="Country / Region" required value={form.country} onChange={update("country")} testid="form-c-country" error={fe.country} />
        <Field label="Organization / Institution" value={form.organization} onChange={update("organization")} testid="form-c-org" />
        <Field label="Role / Designation" value={form.role} onChange={update("role")} testid="form-c-role" />
        <Select label="Contribution Type" required value={form.contribution_type} onChange={update("contribution_type")} options={CONTRIBUTION_TYPES} testid="form-c-type" error={fe.contribution_type} />
        <Select label="Related Book / Topic" value={form.related_topic} onChange={update("related_topic")} options={RELATED_BOOK} testid="form-c-topic" />
        <Field label="Title of Submission" required value={form.title} onChange={update("title")} testid="form-c-title" error={fe.title} />
      </div>
      <TextArea label="Message / Contribution Text" required rows={7} value={form.message} onChange={update("message")} testid="form-c-message" placeholder="Share your contribution, reflection, or proposal." error={fe.message} />
      <div className="rounded-sm border border-dashed border-black/15 px-4 py-3 text-xs text-[#9CA3AF] italic">
        File upload — coming soon. For now please paste a link to your draft (Google Drive, Dropbox, arXiv, etc.) inside the message.
      </div>
      <Consent testid="form-c-consent" checked={form.consent} onChange={update("consent")} label="I grant BSCA permission to review my contribution per the Privacy Policy." error={fe.consent} />
      <FormError testid="form-c-error" message={err} />
      <button type="submit" disabled={busy} data-testid="btn-submit-contribution" className="bsca-lift inline-flex items-center bg-[#050B14] text-white font-medium px-8 py-4 rounded-sm hover:bg-[#0A1120] disabled:opacity-60">
        {busy ? "Submitting…" : "Submit Contribution"} <ArrowRight className="h-4 w-4 ml-2" />
      </button>
    </form>
  );
}

/* ---------------- FORM 3 — Collaboration ---------------- */
function CollaborationForm() {
  const initial = {
    full_name: "", email: "", country: "", organization: "", role: "",
    collaboration_type: "", research_domain: "", message: "", consent: false,
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fe, setFe] = useState({});
  const [done, setDone] = useState(false);
  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
    if (fe[k]) setFe({ ...fe, [k]: undefined });
  };
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const errs = runFieldValidation({
      full_name: [[form.full_name.trim().length > 1, "Please enter your full name."]],
      email: [
        [!!form.email.trim(), "Email is required."],
        [EMAIL_RE.test(form.email.trim()), "Enter a valid email address."],
      ],
      country: [[!!form.country.trim(), "Please enter your country."]],
      organization: [[!!form.organization.trim(), "Please enter your organization or institution."]],
      collaboration_type: [[!!form.collaboration_type, "Please select a collaboration type."]],
      research_domain: [[!!form.research_domain, "Please select a research domain."]],
      message: [[form.message.trim().length >= 20, "Please share a longer proposal (at least 20 characters)."]],
      consent: [[form.consent, "Please consent to BSCA reviewing this proposal."]],
    });
    setFe(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await formsApi.submit("collaboration", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "We couldn't submit right now. Please try again in a moment.");
    } finally { setBusy(false); }
  };
  if (done) return <SuccessCard testid="collaboration-success" title="Proposal received." body="Thank you. The BSCA team reviews collaboration proposals in cycles and will respond as appropriate." onAnother={() => { setForm(initial); setDone(false); setFe({}); }} />;
  return (
    <form onSubmit={submit} data-testid="collaboration-form" className="max-w-4xl mx-auto bg-white border border-black/10 p-7 md:p-10 space-y-6 rounded-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full Name" required value={form.full_name} onChange={update("full_name")} testid="form-cb-name" autoComplete="name" error={fe.full_name} />
        <Field label="Email Address" type="email" required value={form.email} onChange={update("email")} testid="form-cb-email" autoComplete="email" error={fe.email} />
        <Field label="Country / Region" required value={form.country} onChange={update("country")} testid="form-cb-country" error={fe.country} />
        <Field label="Organization / Institution" required value={form.organization} onChange={update("organization")} testid="form-cb-org" error={fe.organization} />
        <Field label="Role / Designation" value={form.role} onChange={update("role")} testid="form-cb-role" />
        <Select label="Collaboration Type" required value={form.collaboration_type} onChange={update("collaboration_type")} options={COLLAB_TYPES} testid="form-cb-type" error={fe.collaboration_type} />
        <Select label="Research Domain" required value={form.research_domain} onChange={update("research_domain")} options={RESEARCH_DOMAINS_OPTS} testid="form-cb-domain" error={fe.research_domain} />
      </div>
      <TextArea label="Collaboration Proposal / Message" required rows={7} value={form.message} onChange={update("message")} testid="form-cb-message" placeholder="Outline your proposal — scope, intent, scale, timeline, and any references." error={fe.message} />
      <Consent testid="form-cb-consent" checked={form.consent} onChange={update("consent")} label="I consent to BSCA reviewing this proposal and storing my information per the Privacy Policy." error={fe.consent} />
      <FormError testid="form-cb-error" message={err} />
      <button type="submit" disabled={busy} data-testid="btn-submit-collaboration" className="bsca-lift inline-flex items-center bg-[#050B14] text-white font-medium px-8 py-4 rounded-sm hover:bg-[#0A1120] disabled:opacity-60">
        {busy ? "Submitting…" : "Propose Collaboration"} <ArrowRight className="h-4 w-4 ml-2" />
      </button>
    </form>
  );
}

/* ---------------- FORM 4 — Contact ---------------- */
function ContactForm() {
  const initial = {
    name: "", email: "", organization: "", country: "",
    inquiry_type: "", message: "", consent: false,
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [fe, setFe] = useState({});
  const [done, setDone] = useState(false);
  const update = (k) => (e) => {
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
    if (fe[k]) setFe({ ...fe, [k]: undefined });
  };
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const errs = runFieldValidation({
      name: [[form.name.trim().length > 1, "Please enter your name."]],
      email: [
        [!!form.email.trim(), "Email is required."],
        [EMAIL_RE.test(form.email.trim()), "Enter a valid email address."],
      ],
      inquiry_type: [[!!form.inquiry_type, "Please select an inquiry type."]],
      message: [[form.message.trim().length >= 10, "Please share a longer message (at least 10 characters)."]],
      consent: [[form.consent, "Please consent to BSCA storing this information."]],
    });
    setFe(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      await formsApi.submit("contact", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "We couldn't send your message right now. Please try again in a moment.");
    } finally { setBusy(false); }
  };
  if (done) return <SuccessCard testid="contact-success" title="Message received." body="Thank you. The BSCA team will respond as soon as possible." onAnother={() => { setForm(initial); setDone(false); setFe({}); }} />;
  return (
    <form onSubmit={submit} data-testid="contact-form" className="max-w-4xl mx-auto bg-white border border-black/10 p-7 md:p-10 space-y-6 rounded-sm">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Name" required value={form.name} onChange={update("name")} testid="form-contact-name" autoComplete="name" error={fe.name} />
        <Field label="Email Address" type="email" required value={form.email} onChange={update("email")} testid="form-contact-email" autoComplete="email" error={fe.email} />
        <Field label="Organization" value={form.organization} onChange={update("organization")} testid="form-contact-org" />
        <Field label="Country / Region" value={form.country} onChange={update("country")} testid="form-contact-country" />
        <Select label="Inquiry Type" required value={form.inquiry_type} onChange={update("inquiry_type")} options={INQUIRY_TYPES} testid="form-contact-type" error={fe.inquiry_type} />
      </div>
      <TextArea label="Message" required rows={7} value={form.message} onChange={update("message")} testid="form-contact-message" placeholder="How can we help?" error={fe.message} />
      <Consent testid="form-contact-consent" checked={form.consent} onChange={update("consent")} label="I consent to BSCA storing this information per the Privacy Policy." error={fe.consent} />
      <FormError testid="form-contact-error" message={err} />
      <button type="submit" disabled={busy} data-testid="btn-submit-contact" className="bsca-lift inline-flex items-center bg-[#050B14] text-white font-medium px-8 py-4 rounded-sm hover:bg-[#0A1120] disabled:opacity-60">
        {busy ? "Sending…" : "Send Message"} <ArrowRight className="h-4 w-4 ml-2" />
      </button>
    </form>
  );
}
